'use client';

import type { ReactNode } from 'react';
import type { FeatureFlagToggle } from '@/libs/feature-flags/types';
import { FlagProvider } from '@unleash/nextjs/client';
import { Env } from '@/consts/Env';
import {
  UNLEASH_APP_NAME,
  UNLEASH_CLIENT_REFRESH_INTERVAL_SECONDS,
} from '@/libs/feature-flags/config';

// FlagProvider requires a complete config even when startClient is false.
// These values are never requested because refresh is disabled in that mode.
const DISABLED_CLIENT_URL = 'http://localhost';
const DISABLED_CLIENT_KEY = 'not-configured';

interface Props {
  children: ReactNode;
  bootstrap?: FeatureFlagToggle[];
}

export function FeatureFlagsProvider({ children, bootstrap = [] }: Props) {
  const url = Env.NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL;
  const clientKey = Env.NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN;
  const canPoll = Boolean(url && clientKey);

  return (
    <FlagProvider
      startClient={canPoll}
      config={{
        url: url ?? DISABLED_CLIENT_URL,
        clientKey: clientKey ?? DISABLED_CLIENT_KEY,
        appName: UNLEASH_APP_NAME,
        bootstrap,
        bootstrapOverride: true,
        refreshInterval: UNLEASH_CLIENT_REFRESH_INTERVAL_SECONDS,
        disableRefresh: !canPoll,
        // Flag polling stays enabled. We only opt out of SDK usage/impact
        // metrics to avoid another periodic request from every open tab.
        disableMetrics: true,
      }}
    >
      {children}
    </FlagProvider>
  );
}
